// Тренажёр — simulatorul de reluare: piaţa dintr-un moment din trecut, bară cu bară.
//
// Ideea e cea de la Preplay: iei o bucată reală din istoria unei monede, o derulezi în
// ritmul tău şi tranzacţionezi pe ea ca pe piaţa vie — ordine de piaţă, limită şi stop,
// cu stop-loss şi take-profit, sold şi istoric. Nu se riscă nimic, dar greşelile se văd
// la fel de bine.
//
// **Viitorul stă pe server.** Browserul primeşte doar barele de până la „acum"-ul
// reluării şi le cere pe următoarele când avansează. Ar fi fost mult mai simplu să
// aducem toată fereastra şi să o descoperim local, dar atunci continuarea ar sta în
// pagină, la o comandă distanţă în consolă — iar un antrenament în care poţi trage cu
// ochiul nu antrenează nimic.
//
// Ritmul: o cerere la cel mult două pe secundă, indiferent de viteză. La 10× nu cerem
// de zece ori pe secundă câte o bară, ci de două ori câte cinci.

import { el, clear, fmtPrice, fmtVolume, bus } from '../core/util.js';
import { icon } from '../ui/icons.js';
import { Chart } from '../chart/chart.js';
import { session } from '../core/session.js';
import { toast } from '../ui/components.js';
import { attachSearchDropdown } from '../ui/search-dropdown.js';

const TIMEFRAMES = ['1m', '5m', '15m', '1h'];
const VITEZE = [0.5, 1, 2, 4, 10];

/** Ce se poate cere la „găseşte-mi o situaţie" — aceleaşi tipuri ca în fila Формации. */
const PATTERNURI = [
  ['', 'Все паттерны'],
  ['breakout', 'Пробой уровня'],
  ['false_breakout', 'Ложный пробой'],
  ['bounce', 'Отскок от уровня'],
  ['squeeze', 'Сжатие у уровня'],
  ['impulse', 'Импульс с объёмом'],
];

const json = async (path, body) => {
  const resp = await fetch(path, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new Error(data.detail || `Ошибка ${resp.status}`);
  return data;
};

export class TrainingSection {
  constructor(app) {
    this.app = app;
    this.tf = '1m';
    this.viteza = 1;
    this.ruleaza = false;
    this.stare = null;
    this.simbolCerut = '';
    // `/preplay/?pattern=breakout` — legătura din pagina de strategii deschide antrenorul
    // direct pe tipul citit acolo. Fără asta, „Потренировать" ar duce într-o listă
    // oarecare şi omul ar trebui să caute singur ce tocmai a citit.
    this.pattern = new URLSearchParams(location.search).get('pattern') || '';
  }

  async mount(host) {
    this.root = el('div', { class: 'rp-section' });
    this.root.append(this.#bara(), this.#corp());
    host.append(this.root);

    this.chart = new Chart(this.chartHost, {
      exchange: this.app.exchange, market: this.app.market,
      symbol: 'BTCUSDT', tf: this.tf, indicators: { volume: true }, showLevels: false,
    });
    await this.#nou();
  }

  // ------------------------------------------------------------------ schelet

  #bara() {
    this.cautareEl = el('input', {
      class: 'rp-search', type: 'text', placeholder: 'Монета…',
      onkeydown: (e) => { if (e.key === 'Enter') this.#nou(e.target.value.trim().toUpperCase()); },
    });
    // Aceleaşi sugestii ca peste tot în aplicaţie — nu inventăm o a doua căutare.
    this.desfaCautarea = attachSearchDropdown(this.cautareEl, (r) => {
      this.cautareEl.value = r.symbol;
      this.#nou(r.symbol);
    });

    this.tfEl = el('div', { class: 'rp-tfs' }, TIMEFRAMES.map((t) => el('button', {
      class: `rp-tf${t === this.tf ? ' active' : ''}`, text: t, dataset: { tf: t },
      onclick: () => { this.tf = t; this.#syncTf(); this.#nou(this.stare?.symbol); },
    })));

    this.patternEl = el('select', {
      class: 'rp-select',
      onchange: (e) => { this.pattern = e.target.value; this.simbolCerut = ''; this.#nou(); },
    }, PATTERNURI.map(([v, t]) => el('option', { value: v, text: t, selected: v === this.pattern })));

    this.tituluEl = el('span', { class: 'rp-symbol' });

    return el('div', { class: 'rp-bar' }, [
      el('span', { class: 'rp-logo' }, [icon('clockHistory', 15), el('span', { text: 'Тренажёр' })]),
      el('label', { class: 'rp-search-box' }, [icon('search', 12), this.cautareEl]),
      this.tfEl,
      this.patternEl,
      this.tituluEl,
      el('button', {
        class: 'btn rp-new', title: 'Случайная монета и случайный момент истории',
        onclick: () => this.#nou(),
      }, [icon('refresh', 12), el('span', { text: 'Новый участок' })]),
      el('span', { class: 'rp-demo', text: 'ДЕМО' }),
    ]);
  }

  #corp() {
    this.chartHost = el('div', { class: 'rp-chart' });
    return el('div', { class: 'rp-body' }, [
      el('div', { class: 'rp-left' }, [this.chartHost, this.#controale()]),
      this.#panou(),
    ]);
  }

  /** Bara de sub grafic: derulare, pas, viteză, resetare. */
  #controale() {
    this.playEl = el('button', { class: 'rp-btn rp-play', onclick: () => this.#togglePlay() },
      [icon('play', 12), el('span', { text: 'Play' })]);

    this.vitezaEl = el('div', { class: 'rp-speeds' }, VITEZE.map((v) => el('button', {
      class: `rp-speed${v === this.viteza ? ' active' : ''}`, text: `${v}x`, dataset: { v },
      onclick: () => {
        this.viteza = v;
        [...this.vitezaEl.children].forEach((c) => c.classList.toggle('active', Number(c.dataset.v) === v));
        if (this.ruleaza) { this.#opreste(); this.#porneste(); }
      },
    })));

    this.ceasEl = el('span', { class: 'rp-clock' });

    return el('div', { class: 'rp-controls' }, [
      this.playEl,
      el('button', { class: 'rp-btn', onclick: () => this.#pas(1) },
        [icon('stepForward', 11), el('span', { text: 'Шаг' })]),
      this.vitezaEl,
      this.ceasEl,
      el('button', { class: 'rp-btn rp-reset', onclick: () => this.#reset() },
        [icon('refresh', 11), el('span', { text: 'Сбросить' })]),
    ]);
  }

  /** Panoul din dreapta: sold, formular de ordin, poziţii, istoric. */
  #panou() {
    this.soldEl = el('span', { class: 'rp-money' });
    this.pnlEl = el('span', { class: 'rp-money' });

    this.tipEl = el('div', { class: 'rp-tabs' }, [
      ['market', 'Рыночная'], ['limit', 'Лимит'], ['stop', 'Стоп'],
    ].map(([id, label]) => el('button', {
      class: `rp-tab${id === 'market' ? ' active' : ''}`, text: label, dataset: { id },
      onclick: () => {
        this.tipOrdin = id;
        [...this.tipEl.children].forEach((c) => c.classList.toggle('active', c.dataset.id === id));
        this.pretBox.style.display = id === 'market' ? 'none' : '';
      },
    })));
    this.tipOrdin = 'market';

    this.cantEl = el('input', { class: 'rp-input', type: 'number', value: 1000, min: 0, step: 10 });
    this.unitEl = this.#comutator(['USDT', 'МОНЕТ'], 'USDT', () => this.#syncCant());
    this.cantHint = el('div', { class: 'rp-hint' });
    this.cantEl.oninput = () => this.#syncCant();

    this.pretEl = el('input', { class: 'rp-input', type: 'number', placeholder: 'Цена ордера', step: 'any' });
    this.pretBox = el('div', { class: 'rp-field' }, [
      el('span', { class: 'rp-label', text: 'ЦЕНА ОРДЕРА' }), this.pretEl,
    ]);
    this.pretBox.style.display = 'none';

    this.slEl = el('input', { class: 'rp-input', type: 'number', placeholder: 'напр. 1', step: 'any' });
    this.slUnit = this.#comutator(['ЦЕНА', '%'], '%');
    this.tpEl = el('input', { class: 'rp-input', type: 'number', placeholder: 'напр. 3', step: 'any' });
    this.tpUnit = this.#comutator(['ЦЕНА', '%'], '%');

    this.pozEl = el('div', { class: 'rp-list' });
    this.pozCount = el('span', { class: 'rp-count' });
    this.istEl = el('div', { class: 'rp-list' });
    this.istCount = el('span', { class: 'rp-count' });
    this.statEl = el('div', { class: 'rp-stats' });

    return el('aside', { class: 'rp-panel' }, [
      el('div', { class: 'rp-card' }, [
        el('div', { class: 'rp-row' }, [el('span', { class: 'rp-k', text: 'БАЛАНС' }), this.soldEl]),
        el('div', { class: 'rp-row' }, [
          el('span', { class: 'rp-k', text: 'НЕРЕАЛИЗОВАННЫЙ PNL' }), this.pnlEl,
        ]),
      ]),

      this.tipEl,

      el('div', { class: 'rp-field' }, [
        el('div', { class: 'rp-label-row' }, [
          el('span', { class: 'rp-label', text: 'КОЛИЧЕСТВО' }), this.unitEl,
        ]),
        this.cantEl, this.cantHint,
      ]),
      this.pretBox,

      el('div', { class: 'rp-field' }, [
        el('div', { class: 'rp-label-row' }, [
          el('span', { class: 'rp-label', text: 'СТОП-ЛОСС' }), this.slUnit,
        ]),
        this.slEl,
      ]),
      el('div', { class: 'rp-field' }, [
        el('div', { class: 'rp-label-row' }, [
          el('span', { class: 'rp-label', text: 'ТЕЙК-ПРОФИТ' }), this.tpUnit,
        ]),
        this.tpEl,
      ]),

      el('div', { class: 'rp-sides' }, [
        el('button', { class: 'rp-side long', text: 'LONG', onclick: () => this.#ordin('long') }),
        el('button', { class: 'rp-side short', text: 'SHORT', onclick: () => this.#ordin('short') }),
      ]),

      el('div', { class: 'rp-sec' }, [el('span', { text: 'ОТКРЫТЫЕ' }), this.pozCount]),
      this.pozEl,
      el('div', { class: 'rp-sec' }, [el('span', { text: 'ИСТОРИЯ' }), this.istCount]),
      this.statEl,
      this.istEl,
    ]);
  }

  /** Comutator din două stări (USDT/МОНЕТ, ЦЕНА/%). */
  #comutator(optiuni, initial, onChange) {
    const box = el('div', { class: 'rp-toggle' }, optiuni.map((o) => el('button', {
      class: `rp-toggle-b${o === initial ? ' active' : ''}`, text: o, dataset: { v: o },
      onclick: () => {
        [...box.children].forEach((c) => c.classList.toggle('active', c.dataset.v === o));
        box.dataset.value = o;
        onChange?.(o);
      },
    })));
    box.dataset.value = initial;
    return box;
  }

  // ------------------------------------------------------------------ sesiune

  async #nou(symbol = '') {
    this.#opreste();
    try {
      const cerut = symbol || this.simbolCerut || '';
      const d = await json('/api/replay/new', {
        exchange: this.app.exchange, market: this.app.market,
        symbol: cerut, tf: this.tf,
        // Tipul cerut are efect doar când nu s-a cerut o monedă anume: altfel omul a
        // spus deja ce vrea să vadă, iar căutarea l-ar duce în altă parte.
        pattern: cerut ? '' : this.pattern,
      });
      this.simbolCerut = symbol || '';
      this.#aplica(d, { plin: true });
      toast(d.label ? `${d.symbol} · ${d.label}` : `${d.symbol} · ${d.tf}`, 'info');
    } catch (err) {
      toast(err.message, 'error');
    }
  }

  async #reset() {
    if (!this.stare) return;
    this.#opreste();
    try { this.#aplica(await json('/api/replay/reset', { token: this.stare.token }), { plin: true }); }
    catch (err) { toast(err.message, 'error'); }
  }

  /** Aplică un răspuns al serverului: barele noi pe grafic, restul în panou. */
  #aplica(d, { plin = false } = {}) {
    this.stare = d;
    if (plin) {
      this.chart.symbol = d.symbol;
      this.chart.tf = d.tf;
      this.chart.setBars(d.bars);
      this.chart.resetView();
    } else {
      for (const b of d.bars) this.chart.pushBar(b);
    }
    for (const ev of d.events || []) {
      if (ev.type === 'sl') toast(`Стоп-лосс: ${ev.pnl > 0 ? '+' : ''}${ev.pnl}$`, 'error');
      if (ev.type === 'tp') toast(`Тейк-профит: +${ev.pnl}$`, 'success');
      if (ev.type === 'fill') toast(`Ордер исполнен: ${ev.side} @ ${fmtPrice(ev.price)}`, 'info');
    }
    this.#render();
  }

  async #pas(n = 1) {
    if (!this.stare || this.inCurs) return;
    this.inCurs = true;
    try {
      const d = await json('/api/replay/step', { token: this.stare.token, count: n });
      if (!d.bars.length) { this.#opreste(); toast('История закончилась — возьмите новый участок', 'info'); }
      this.#aplica(d);
    } catch (err) {
      this.#opreste();
      toast(err.message, 'error');
    } finally {
      this.inCurs = false;
    }
  }

  #togglePlay() { this.ruleaza ? this.#opreste() : this.#porneste(); }

  #porneste() {
    if (!this.stare) return;
    this.ruleaza = true;
    clear(this.playEl).append(icon('pause', 12), el('span', { text: 'Пауза' }));
    // O cerere la cel mult două pe secundă, oricare ar fi viteza: la 10× cerem de două
    // ori câte cinci bare, nu de zece ori câte una.
    const peSecunda = Math.min(this.viteza, 2);
    const cate = Math.max(1, Math.round(this.viteza / peSecunda));
    this.timer = setInterval(() => this.#pas(cate), 1000 / peSecunda);
  }

  #opreste() {
    this.ruleaza = false;
    clearInterval(this.timer);
    this.timer = null;
    if (this.playEl) clear(this.playEl).append(icon('play', 12), el('span', { text: 'Play' }));
  }

  // ------------------------------------------------------------------- ordine

  #syncCant() {
    const pret = this.stare?.price || 0;
    const v = Number(this.cantEl.value) || 0;
    const inUsdt = this.unitEl.dataset.value === 'USDT';
    this.cantHint.textContent = pret
      ? (inUsdt ? `≈ ${(v / pret).toFixed(6)} монет` : `≈ ${fmtVolume(v * pret)}`)
      : '';
  }

  /** Traduce un câmp „preţ sau procent" într-un preţ absolut. */
  #nivel(input, box, side, tip) {
    const v = Number(input.value);
    if (!v) return 0;
    if (box.dataset.value === 'ЦЕНА') return v;
    const pret = this.stare?.price || 0;
    // Procentul se socoteşte de la preţul de intrare, în direcţia care are sens:
    // stopul sub intrare la long, deasupra la short. Aşa un „1" scris în stop nu
    // deschide niciodată o poziţie deja pierdută.
    const jos = (side === 'long') === (tip === 'sl');
    return pret * (jos ? 1 - v / 100 : 1 + v / 100);
  }

  async #ordin(side) {
    if (!this.stare) return;
    const pret = this.stare.price || 0;
    const brut = Number(this.cantEl.value) || 0;
    const qty = this.unitEl.dataset.value === 'USDT' ? (pret ? brut / pret : 0) : brut;
    if (qty <= 0) { toast('Укажите количество', 'error'); return; }

    try {
      const d = await json('/api/replay/order', {
        token: this.stare.token, side, kind: this.tipOrdin, qty,
        price: this.tipOrdin === 'market' ? 0 : Number(this.pretEl.value) || 0,
        sl: this.#nivel(this.slEl, this.slUnit, side, 'sl'),
        tp: this.#nivel(this.tpEl, this.tpUnit, side, 'tp'),
      });
      this.stare = d;
      this.#render();
      toast(this.tipOrdin === 'market'
        ? `${side.toUpperCase()} открыт @ ${fmtPrice(d.price)}`
        : 'Ордер поставлен', 'success');
    } catch (err) {
      toast(err.message, 'error');
    }
  }

  async #inchide(id) {
    try { this.stare = await json('/api/replay/close', { token: this.stare.token, id }); this.#render(); }
    catch (err) { toast(err.message, 'error'); }
  }

  async #anuleaza(id) {
    try { this.stare = await json('/api/replay/cancel', { token: this.stare.token, id }); this.#render(); }
    catch (err) { toast(err.message, 'error'); }
  }

  // ------------------------------------------------------------------ randare

  #syncTf() {
    [...this.tfEl.children].forEach((c) => c.classList.toggle('active', c.dataset.tf === this.tf));
  }

  #render() {
    const d = this.stare;
    if (!d) return;
    const bani = (v) => `${v < 0 ? '−' : ''}$${Math.abs(v).toFixed(2)}`;

    this.tituluEl.textContent = d.label ? `${d.symbol} · ${d.label}` : `${d.symbol} · ${d.tf}`;
    this.soldEl.textContent = bani(d.balance);
    this.pnlEl.textContent = bani(d.floating);
    this.pnlEl.className = `rp-money ${d.floating > 0 ? 'up' : d.floating < 0 ? 'down' : ''}`;
    this.ceasEl.textContent = d.at
      ? new Date(d.at * 1000).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit',
        year: '2-digit', hour: '2-digit', minute: '2-digit' })
      : '';
    this.#syncCant();

    // Poziţii deschise
    clear(this.pozEl);
    this.pozCount.textContent = `(${d.positions.length})`;
    if (!d.positions.length && !d.orders.length) {
      this.pozEl.append(el('div', { class: 'rp-empty', text: 'Нет позиций' }));
    }
    for (const p of d.positions) {
      this.pozEl.append(el('div', { class: 'rp-pos' }, [
        el('span', { class: `rp-badge ${p.side}`, text: p.side.toUpperCase() }),
        el('div', { class: 'rp-pos-main' }, [
          el('span', { class: 'rp-pos-t', text: `${p.qty.toFixed(4)} @ ${fmtPrice(p.entry)}` }),
          el('span', { class: 'rp-pos-x', text: [
            p.sl ? `SL ${fmtPrice(p.sl)}` : null, p.tp ? `TP ${fmtPrice(p.tp)}` : null,
          ].filter(Boolean).join(' · ') || 'без стопа' }),
        ]),
        el('span', { class: `rp-pnl ${p.pnl >= 0 ? 'up' : 'down'}`, text: `${bani(p.pnl)} (${p.pnl_pct >= 0 ? '+' : ''}${p.pnl_pct}%)` }),
        el('button', { class: 'rp-x', title: 'Закрыть', onclick: () => this.#inchide(p.id) }, [icon('close', 10)]),
      ]));
    }
    for (const o of d.orders) {
      this.pozEl.append(el('div', { class: 'rp-pos pending' }, [
        el('span', { class: `rp-badge ${o.side}`, text: o.side.toUpperCase() }),
        el('div', { class: 'rp-pos-main' }, [
          el('span', { class: 'rp-pos-t', text: `${o.kind === 'limit' ? 'Лимит' : 'Стоп'} ${fmtPrice(o.price)}` }),
          el('span', { class: 'rp-pos-x', text: `${o.qty.toFixed(4)} · ждёт` }),
        ]),
        el('button', { class: 'rp-x', title: 'Отменить', onclick: () => this.#anuleaza(o.id) }, [icon('close', 10)]),
      ]));
    }

    // Istoric şi rezultat
    const s = d.stats;
    clear(this.statEl);
    if (s.trades) {
      this.statEl.append(
        el('span', { class: `rp-stat ${s.pnl >= 0 ? 'up' : 'down'}`, text: `${bani(s.pnl)}` }),
        el('span', { class: 'rp-stat-x', text: `${s.trades} сделок · винрейт ${s.winrate}%` }),
      );
    }
    clear(this.istEl);
    this.istCount.textContent = `(${s.trades})`;
    for (const t of d.history) {
      this.istEl.append(el('div', { class: 'rp-hist' }, [
        el('span', { class: `rp-badge ${t.side}`, text: t.side.toUpperCase() }),
        el('span', { class: 'rp-hist-x', text: `${fmtPrice(t.entry)} → ${fmtPrice(t.exit)}` }),
        el('span', { class: 'rp-hist-r', text: { sl: 'стоп', tp: 'тейк', manual: 'вручную' }[t.reason] }),
        el('span', { class: `rp-pnl ${t.pnl >= 0 ? 'up' : 'down'}`, text: `${t.pnl >= 0 ? '+' : ''}${t.pnl.toFixed(2)}` }),
      ]));
    }
  }

  unmount() {
    this.#opreste();
    this.desfaCautarea?.();
    this.chart?.destroy();
    this.root?.remove();
  }
}
